import { useModal } from '@ebay/nice-modal-react'
import { Link, useNavigate } from '@tanstack/router'
import axios from 'axios'
import Autoplay from 'embla-carousel-autoplay'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import chest from '../../assets/coin-shower/chest.gif'
import useCoinGame from '../../hooks/useCoinGame'
import useDimensions from '../../hooks/useDimensions'
import { useEmblaCarouselWithIndex } from '../../hooks/useEmblaCarouselWithIndex'
import useTranslateImage from '../../hooks/useTranslateImage'
import { useBoundStore } from '../../store/store'
import type { Language } from '../../types/common'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import LogInModal from '../common/log-in-modal'
import ReferNowModal from '../referral-v2/refer-now-modal'
import WalletManagementModal from '../wallet-management/wallet-management-modal'

interface BannerItem {
  img_es: string
  img_en: string
  img_dark_es: string
  img_dark_en: string
}

interface HomePageBanner {
  default: {
    color: string
    page: 'deposit' | 'play'
    redirect_params: {
      category: string
    }
    terms_en: null | string
    terms_es: null | string
    banner: string
  }
  desktop: BannerItem
  mobile: BannerItem
}

const Banner = () => {
  const translateImage = useTranslateImage()
  const openCoinGame = useCoinGame()
  const referNow = useModal(ReferNowModal)
  const login = useModal(LogInModal)
  const walletManagementModal = useModal(WalletManagementModal)
  const isCoinPlayEnabled = useBoundStore(
    state => state.settings.bonus_coinplay.value,
  )
  const { t, i18n } = useTranslation('homeV2')
  const { language } = i18n
  const theme = useBoundStore(state => state.common.theme)
  const username = useBoundStore(state => state.user.username)

  const { emblaRef, emblaApi, selectedIndex } = useEmblaCarouselWithIndex(
    { loop: true },
    [Autoplay()],
  )

  const navigate = useNavigate()
  const { isDesktop } = useDimensions()
  const [banners, setBanners] = useState<HomePageBanner[]>([])

  const getBannerSrc = useCallback(
    (banner: HomePageBanner) => {
      return banner[isDesktop ? 'desktop' : 'mobile'][
        `img_${theme === 'dark' ? 'dark_' : ''}${language as Language}`
      ]
    },
    [isDesktop, language, theme],
  )

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const fetchBanners = async () => {
      try {
        const res = await api.get<HomePageBanner[]>(
          `/components.php`,
          undefined,
          { signal },
        )
        if (res.data) {
          setBanners(res.data)
        }
      } catch (error) {
        setBanners([])
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchBanners()

    return () => {
      controller.abort()
    }
  }, [])

  return (
    <div className="w-full relative aspect-[13_/_18] pt-[50px] pb-0 px-0 xl:aspect-[9_/_5]">
      <button
        className="absolute flex z-[1] left-0 top-[5px] xl:left-[70px] xl:top-2.5"
        onClick={() => referNow.show()}>
        <img
          className="w-[90px] aspect-[200_/_97] xl:w-[120px]"
          src={translateImage('refer-now')}
          alt="Refer now"
        />
      </button>
      {!!isCoinPlayEnabled && (
        <button
          className="absolute top-[-15px] flex z-[1] left-[75px] xl:left-[170px] xl:-top-3"
          onClick={openCoinGame}>
          <img
            className="w-20 aspect-[1] xl:w-[100px]"
            src={chest}
            alt="Play coin shower game"
          />
        </button>
      )}
      <div style={{ position: 'relative' }}>
        {isDesktop && (
          <div className="flex justify-center items-center flex-col absolute -translate-y-2/4 z-[1] right-5 top-2/4">
            {banners.map((_banner, key) => (
              <div
                className={classNames(
                  'w-[9px] h-[9px] transition-all duration-[0.2s] ease-linear cursor-pointer border mb-[18px] rounded-[50%] border-solid border-txt last:mb-0',
                  key === selectedIndex
                    ? 'bg-[#0E091E] dark:bg-white'
                    : 'bg-transparent',
                )}
                key={key}
                onClick={() => emblaApi?.scrollTo(key)}
              />
            ))}
          </div>
        )}
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {banners.map((banner, index) => (
              <div
                key={index}
                className="embla__slide w-full overflow-hidden relative">
                <link rel="preload" as="image" href={getBannerSrc(banner)} />
                <img
                  className="w-full aspect-[13_/_18] xl:aspect-[9_/_5]"
                  src={getBannerSrc(banner)}
                  alt={banner.default.banner}
                />
                <div className="xl:w-6/12 xl:absolute xl:right-[0%] xl:bottom-[20%]">
                  <button
                    className="uppercase text-xs text-white w-[200px] bg-[#df0033] block text-center mx-auto my-0 px-0 py-[19px] rounded-sm xl:text-[15px] xl:w-[300px] font-Syncopate-Bold"
                    onClick={() => {
                      if (banner.default.page === 'deposit') {
                        if (!username) return login.show()
                        else
                          return walletManagementModal.show({
                            walletManagementDefaultTab: { tab: 0 },
                          })
                      } else if (banner.default.page === 'play') {
                        return navigate({
                          to: '/casino/$category',
                          params: {
                            category:
                              banner?.default?.redirect_params?.category ||
                              'lobby',
                          },
                        })
                      }
                    }}
                    style={{ backgroundColor: banner.default.color }}>
                    {banner.default.page === 'deposit'
                      ? t('deposit-now')
                      : t('start-playing')}
                  </button>
                  {banner.default.terms_es && banner.default.terms_en && (
                    <p className="text-[15px] text-txt text-center mt-3 px-[25px] py-0 xl:text-xl xl:mt-4">
                      <Link
                        to="/promotions/$promoId"
                        params={{
                          promoId:
                            language === 'en'
                              ? banner.default.terms_en
                              : banner.default.terms_es,
                        }}>
                        <span className="text-[15px] text-txt underline xl:text-xl">
                          {t('terms')}
                        </span>
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Banner
